import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleX, Clock, MoreHorizontal, TriangleAlert } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useApi } from '../api/context';
import { PAGE_SIZE, queryKeys, usePager } from '../api/queries';
import type { Deployment, DeploymentResource, ResourceType, RunKind, RunStatus } from '../api/types';
import { usePermission } from '../auth/context';
import { resourceStatusPresentation } from '../components/presentation';
import { ResourceIcon } from '../components/ResourceIcon';
import { useToast } from '../components/toast';
import { CopyButton, ErrorMessage, Loading, Pager, StatusBadge } from '../components/ui';
import { DeployBox } from '../functions/DeployBox';
import { formatJsonValue } from '../lib/format';
import { useCardTable } from '../lib/cardTable';
import { RunSummaryTable } from '../runs/RunSummaryTable';
import { MenuButton, type MenuEntry } from '../shell/Menu';
import { strings } from '../strings';
import { AppStatusChip } from '../variables/AppStatus';
import { deliveryEntry } from '../variables/registry';
import { useAppRollout } from '../variables/useAppRollout';
import type { VariablesData } from '../variables/useVariablesData';

const s = strings.deploymentPage;

function Outputs({ resource }: { resource: DeploymentResource }) {
  const entries = Object.entries(resource.outputs ?? {});
  if (entries.length === 0 && resource.secretOutputs.length === 0) return <>{strings.common.dash}</>;
  return (
    <span className="outputs">
      {entries.map(([key, value]) => {
        const text = formatJsonValue(value);
        return (
          <span key={key} className="output">
            <span className="output-text" title={`${key} ${text}`}>
              <span className="text-fg-muted">{key}</span> <span className="font-mono">{text}</span>
            </span>
            {/^https?:\/\//.test(text) && (
              <CopyButton value={text} label={s.copyOutput(key, resource.resourceId)} />
            )}
          </span>
        );
      })}
      {resource.secretOutputs.map((key) => (
        <span key={key} className="output">
          <span className="text-fg-muted">{key}</span>{' '}
          <span className="badge tone-warning">{strings.common.secret}</span>
        </span>
      ))}
    </span>
  );
}

/** The Variables column (spec 6.4): apps' variable count and issues, opening the window scoped to the app. */
function VariablesCount({
  resourceId,
  variables,
  onOpen,
}: {
  resourceId: string;
  variables: VariablesData;
  onOpen: (resourceId: string) => void;
}) {
  const summary = variables.summary(resourceId);
  if (!summary) return <>{strings.common.dash}</>;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        data-testid={`resource-variables-${resourceId}`}
        aria-label={strings.variables.footerLabel(resourceId, summary.count)}
        onClick={() => onOpen(resourceId)}
      >
        <span className="tabular-nums">{summary.count}</span>
        {summary.errors > 0 && (
          <span className="badge tone-danger">
            <CircleX aria-hidden className="size-3" />
            {summary.errors}
          </span>
        )}
        {summary.warnings > 0 && (
          <span className="badge tone-warning">
            <TriangleAlert aria-hidden className="size-3" />
            {summary.warnings}
          </span>
        )}
      </button>
      {summary.app?.appReferenceStatus && (
        <AppStatusChip app={summary.app} entry={summary.delivery ? deliveryEntry(summary.delivery) : null} />
      )}
    </span>
  );
}

function ResourceMenu({
  deployment,
  resource,
  isApp,
  canRefreshReferences,
  rollout,
  onOpenVariables,
}: {
  deployment: Deployment;
  resource: DeploymentResource;
  isApp: boolean;
  canRefreshReferences: boolean;
  /** The app's rollout (feature spec 15 19.3), next to Refresh references or instead of it. */
  rollout: MenuEntry | null;
  onOpenVariables: (resourceId: string) => void;
}) {
  const api = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const operate = usePermission('operate');
  const id = resource.resourceId;
  const entries: MenuEntry[] = [
    {
      kind: 'action',
      id: 'designer',
      label: s.openInDesigner,
      onSelect: () =>
        navigate(`/blueprints/${deployment.blueprintId}?deployment=${deployment.id}&select=${id}`),
    },
  ];
  if (isApp)
    entries.push({
      kind: 'action',
      id: 'variables',
      label: s.showVariables,
      onSelect: () => onOpenVariables(id),
    });
  if (isApp && canRefreshReferences)
    entries.push({
      kind: 'action',
      id: 'refresh',
      label: s.refreshReferences,
      disabledReason: operate ? undefined : strings.common.requiresRole('operator'),
      onSelect: () =>
        void api
          .refreshReferences(deployment.id, id)
          .then(() => {
            toast.show({ tone: 'success', title: strings.variables.maintenance.refreshed(id) });
            void queryClient.invalidateQueries({ queryKey: queryKeys.variables(deployment.id) });
          })
          .catch((error: unknown) => toast.error(error)),
    });
  if (isApp && rollout) entries.push(rollout);
  if (resource.primaryCloudId) {
    const cloudId = resource.primaryCloudId;
    entries.push({
      kind: 'action',
      id: 'copy',
      label: s.copyCloudId,
      onSelect: () => void navigator.clipboard?.writeText(cloudId).catch(() => {}),
    });
  }
  return (
    <MenuButton
      buttonContent={<MoreHorizontal aria-hidden className="size-4" />}
      buttonLabel={s.resourceActions(id)}
      buttonTitle={s.resourceActions(id)}
      buttonClassName="btn btn-ghost btn-icon"
      menuLabel={s.resourceActions(id)}
      entries={entries}
      placement="below-end"
      portal
    />
  );
}

/** The Overview tab (spec 6.4, mock m07): resources, Deploy boxes, recent runs. */
export function OverviewTab({
  deployment,
  resources,
  resourcesError,
  types,
  variables,
  waitingSources,
  active,
  onOpenVariables,
  onShowRuns,
}: {
  deployment: Deployment;
  resources: DeploymentResource[] | undefined;
  resourcesError: unknown;
  types: ReadonlyMap<string, ResourceType>;
  variables: VariablesData;
  /** Resources with a trigger that waits for code. */
  waitingSources: ReadonlySet<string>;
  active: boolean;
  onOpenVariables: (resourceId: string) => void;
  onShowRuns: () => void;
}) {
  const api = useApi();
  const filterId = useId();
  const [filter, setFilter] = useState('');
  const cardTable = useCardTable();
  const all = useMemo(() => resources ?? [], [resources]);
  const words = filter.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = all.filter((r) => {
    const text = `${r.resourceId} ${r.type} ${types.get(r.type)?.displayName ?? ''}`.toLowerCase();
    return words.every((word) => text.includes(word));
  });
  const recent = useQuery({
    queryKey: [...queryKeys.runSummaries(deployment.id, null), 'recent'],
    queryFn: () => api.listRunSummaries(deployment.id, {}, { limit: 5 }),
    refetchInterval: active ? 3000 : false,
  });
  const rollout = useAppRollout(deployment, variables);
  const refreshable = (resourceId: string) =>
    variables.rows.some((row) => row.resourceId === resourceId && row.delivery?.refreshReferences);

  return (
    <>
      {rollout.dialog}
      <section aria-labelledby="resources-heading" className="mb-6">
        <div className="mb-2 flex flex-wrap items-center gap-3">
          <h2 id="resources-heading" className="text-[15px] font-semibold">
            {s.resources}
          </h2>
          <span className="text-sm text-fg-muted">{s.resourceCount(all.length)}</span>
          <span className="flex-1" />
          <label className="sr-only" htmlFor={filterId}>
            {s.filterResources}
          </label>
          <input
            id={filterId}
            type="search"
            className="input w-56"
            placeholder={s.filterResources}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        {resourcesError ? (
          <ErrorMessage error={resourcesError} />
        ) : !resources ? (
          <Loading />
        ) : all.length === 0 ? (
          <p className="help">{strings.deployments.noResources}</p>
        ) : shown.length === 0 ? (
          <p className="help">{s.noResourceMatch}</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-line bg-surface">
            <table ref={cardTable} className="table table-cards">
              <thead>
                <tr>
                  <th scope="col">{s.resource}</th>
                  <th scope="col">{s.placementColumn}</th>
                  <th scope="col">{s.status}</th>
                  <th scope="col">{s.variables}</th>
                  <th scope="col">{s.outputs}</th>
                  <th scope="col">
                    <span className="sr-only">{s.actions}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((resource) => {
                  const type = types.get(resource.type);
                  return (
                    <tr key={resource.resourceId} data-testid={`resource-row-${resource.resourceId}`}>
                      <td>
                        <span className="flex items-center gap-2">
                          <ResourceIcon name={type?.icon} className="size-4 shrink-0 text-fg-muted" />
                          <span className="font-mono font-semibold whitespace-nowrap">
                            {resource.resourceId}
                          </span>
                          <span className="text-xs text-fg-muted">{type?.displayName ?? resource.type}</span>
                        </span>
                      </td>
                      <td className="whitespace-nowrap">
                        {resource.provider} · {resource.region}
                      </td>
                      <td>
                        <span className="flex flex-wrap items-center gap-1">
                          <StatusBadge presentation={resourceStatusPresentation[resource.status]} />
                          {waitingSources.has(resource.resourceId) && (
                            <span className="badge tone-warning" title={s.triggerBadgeTitle}>
                              <Clock aria-hidden className="size-3" />
                              {s.triggerBadge}
                            </span>
                          )}
                        </span>
                      </td>
                      <td>
                        <VariablesCount
                          resourceId={resource.resourceId}
                          variables={variables}
                          onOpen={onOpenVariables}
                        />
                      </td>
                      <td className="text-xs">
                        <Outputs resource={resource} />
                      </td>
                      <td className="text-right">
                        <ResourceMenu
                          deployment={deployment}
                          resource={resource}
                          isApp={!!type?.injectionProperty}
                          canRefreshReferences={refreshable(resource.resourceId)}
                          rollout={rollout.menuEntry(resource.resourceId)}
                          onOpenVariables={onOpenVariables}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div id="deploy-boxes">
          {all
            .filter((r) => r.type === 'compute.function')
            .map((r) => (
              <details key={r.resourceId} open className="mt-3">
                <summary className="cursor-pointer text-sm font-semibold">
                  {s.deployBoxes} · <span className="font-mono">{r.resourceId}</span>
                </summary>
                <DeployBox resourceId={r.resourceId} resource={r} hasDeployment />
              </details>
            ))}
        </div>
      </section>

      <section aria-labelledby="recent-runs-heading">
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <h2 id="recent-runs-heading" className="text-[15px] font-semibold">
            {s.recentRuns}
          </h2>
          <button type="button" className="link text-sm" onClick={onShowRuns}>
            {s.allRuns}
          </button>
        </div>
        {recent.error ? (
          <ErrorMessage error={recent.error} />
        ) : !recent.data ? (
          <Loading />
        ) : recent.data.items.length === 0 ? (
          <p className="help">{strings.deployments.noRuns}</p>
        ) : (
          <RunSummaryTable
            runs={recent.data.items}
            showDeployment={false}
            labelledBy="recent-runs-heading"
            deploymentName={deployment.name}
          />
        )}
      </section>
    </>
  );
}

const KINDS: readonly RunKind[] = ['plan', 'apply', 'destroy', 'refresh'];
const STATUSES = ['active', 'completed', 'failed', 'canceled'] as const;

/** The Runs tab (spec 6.4): the deployment's run summaries with kind and status filters, paged. */
export function RunsTab({ deployment, active }: { deployment: Deployment; active: boolean }) {
  const api = useApi();
  const pager = usePager();
  const kindId = useId();
  const statusId = useId();
  const [kind, setKind] = useState<RunKind | ''>('');
  const [status, setStatus] = useState<(typeof STATUSES)[number] | ''>('');
  const runs = useQuery({
    queryKey: [...queryKeys.runSummaries(deployment.id, pager.cursor), kind, status],
    queryFn: () =>
      api.listRunSummaries(
        deployment.id,
        { kind: kind || null, status: (status || null) as RunStatus | 'active' | null },
        { limit: PAGE_SIZE, cursor: pager.cursor },
      ),
    placeholderData: keepPreviousData,
    refetchInterval: active ? 3000 : false,
  });
  return (
    <section aria-labelledby="runs-tab-heading">
      <h2 id="runs-tab-heading" className="sr-only">
        {s.runs}
      </h2>
      <div role="group" aria-label={strings.runsList.filters} className="list-toolbar">
        <div className="filter">
          <label htmlFor={kindId}>{s.kindFilter}</label>
          <select
            id={kindId}
            className="input"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as RunKind | '');
              pager.reset();
            }}
          >
            <option value="">{strings.common.any}</option>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {strings.status.runKind[k]}
              </option>
            ))}
          </select>
        </div>
        <div className="filter">
          <label htmlFor={statusId}>{s.statusFilter}</label>
          <select
            id={statusId}
            className="input"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as (typeof STATUSES)[number] | '');
              pager.reset();
            }}
          >
            <option value="">{strings.common.any}</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {strings.runsList.statusValues[value]}
              </option>
            ))}
          </select>
        </div>
        <span className="flex-1" />
        <Link className="link self-center text-sm" to={`/runs?deployment=${deployment.id}`}>
          {s.openInRuns}
        </Link>
      </div>
      {runs.error ? (
        <ErrorMessage error={runs.error} />
      ) : !runs.data ? (
        <Loading />
      ) : runs.data.items.length === 0 ? (
        <p className="help">{kind || status ? s.noRunMatch : strings.deployments.noRuns}</p>
      ) : (
        <RunSummaryTable
          runs={runs.data.items}
          showDeployment={false}
          labelledBy="runs-tab-heading"
          deploymentName={deployment.name}
        />
      )}
      {runs.data && (
        <Pager
          hasPrevious={pager.hasPrevious}
          nextCursor={runs.data.nextCursor}
          onPrevious={pager.previous}
          onNext={pager.next}
        />
      )}
    </section>
  );
}
